import { COLOR_RULE, INPUT_TYPE, PLOT_TYPE, THEME_COLOR, MA_TYPE, PRICE_SOURCE } from "./indicatorTypes";
import { PALETTE } from "./palette";

const DEFAULT_LINE_WIDTH_PX = 1;

/**
 * Short builders of indicator definitions (definitions/*.js), so every
 * definition reads like the PO settings modal.
 */

/** Whole number input, e.g. a period. */
export const periodInput = (key, label, defaultValue) => ({
  key,
  label,
  type: INPUT_TYPE.NUMBER,
  defaultValue,
  min: 1,
  step: 1,
  isInteger: true,
});

/** Fractional number input, e.g. a deviation or a multiplier. */
export const numberInput = (key, label, defaultValue, { min = 0, step = 0.01 } = {}) => ({
  key,
  label,
  type: INPUT_TYPE.NUMBER,
  defaultValue,
  min,
  step,
  isInteger: false,
});

export const maTypeInput = (key = "maType", label = "MA Type", defaultValue = MA_TYPE.SMA) => ({
  key,
  label,
  type: INPUT_TYPE.SELECT,
  defaultValue,
  options: Object.values(MA_TYPE),
});

export const sourceInput = (key = "source", label = "Source", defaultValue = PRICE_SOURCE.CLOSE) => ({
  key,
  label,
  type: INPUT_TYPE.SELECT,
  defaultValue,
  options: Object.values(PRICE_SOURCE),
});

/**
 * @param {string} key - name of the values in the calculated series
 * @param {string} label - as in the Styles tab of PO
 * @param {string} color - PALETTE name or THEME_COLOR value
 * @param {object} [options] - width (CSS px), shift (bars, a number or (inputs) => number)
 */
export const linePlot = (key, label, color, { width = DEFAULT_LINE_WIDTH_PX, shift = 0 } = {}) => ({
  key,
  label,
  type: PLOT_TYPE.LINE,
  color,
  width,
  shift,
});

/**
 * @param {string} key
 * @param {string} label
 * @param {string} colorRule - COLOR_RULE value; COLOR_RULE.SINGLE has no down colour
 * @param {object} [colors] - up / down: PALETTE names or THEME_COLOR values;
 *   LIME / RED by default, as in PO
 */
export const histogramPlot = (
  key,
  label,
  colorRule,
  { up = PALETTE.LIME, down = PALETTE.RED } = {},
) => ({
  key,
  label,
  type: PLOT_TYPE.HISTOGRAM,
  color: up,
  downColor: colorRule === COLOR_RULE.SINGLE ? null : down,
  colorRule,
  width: DEFAULT_LINE_WIDTH_PX,
  shift: 0,
});

export const dotsPlot = (key, label, color) => ({
  key,
  label,
  type: PLOT_TYPE.DOTS,
  color,
  width: DEFAULT_LINE_WIDTH_PX,
  shift: 0,
});

/** @param {boolean} isAbove - marks above the highs (true) or below the lows */
export const markersPlot = (key, label, color, isAbove) => ({
  key,
  label,
  type: PLOT_TYPE.MARKERS,
  color,
  isAbove,
  width: DEFAULT_LINE_WIDTH_PX,
  shift: 0,
});

/** @param {string[]} between - keys of the two lines the background fills */
export const fillPlot = (key, label, color, between) => ({
  key,
  label,
  type: PLOT_TYPE.FILL,
  color,
  between,
  width: 0,
  shift: 0,
});

/**
 * Horizontal level of an oscillator (RSI 70 / 30), each with its own colour
 * and width as in PO.
 *
 * @param {number} value
 * @param {string} [color] - PALETTE name or THEME_COLOR value
 * @param {number} [width] - CSS px
 */
export const level = (value, color = THEME_COLOR.LEVEL, width = DEFAULT_LINE_WIDTH_PX) => ({ value, color, width });
